import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { IonicModule, Platform } from '@ionic/angular/lazy';
import { filter, map } from 'rxjs/operators';
import { register } from 'swiper/element';
import {
  AdjustService,
  AppTitleService,
  AuthService,
  FirebaseAnalyticsService,
  PushNotificationService,
} from '@app/shared/services';
import { Capacitor } from '@capacitor/core';
import { FirebaseCrashlyticsService } from '@app/shared/services/firebase-crashlytics.service';
import { environment } from '@environments/environment';

register(); // ← これが必須！

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-root',
  template: '<ion-router-outlet></ion-router-outlet>',
  standalone: true,
  imports: [IonicModule],
})
export class AppComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly titleService = inject(AppTitleService);
  private readonly authService = inject(AuthService);
  private readonly firebaseAnalyticsService = inject(FirebaseAnalyticsService);
  private readonly pushNotificationService = inject(PushNotificationService);
  private readonly adjustService = inject(AdjustService);
  private readonly platform = inject(Platform);
  private readonly crashlytics = inject(FirebaseCrashlyticsService);

  // Push 通知の初期化が完了したかを管理（重複呼び出し防止）
  private pushInitDone = false;

  constructor() {
    this.setupTitleChangeOnNavigation();
    this.setupPushInitOnNavigation();

    this.platform.ready().then(async () => {
      if (Capacitor.getPlatform() === 'android') {
        document.documentElement.classList.add('platform-android');
        // overlaysWebView: false を capacitor.config.ts で設定済み
        // WebView はステータスバーの下から描画されるため、手動パディング不要
      } else if (Capacitor.getPlatform() === 'ios') {
        document.documentElement.classList.add('platform-ios');
      }
    });
  }

  ngOnInit() {
    void this.initializeApp();
  }

  /**
   * アプリ起動時の初期化処理
   * - Analytics のユーザー設定など、Push 初期化以外を実施
   */
  private async initializeApp(): Promise<void> {
    // ネイティブ環境のみ Crashlytics を有効化（Web 実行では送信しない）
    const isNative = Capacitor.getPlatform() !== 'web';
    await this.crashlytics.setEnabled(isNative);
    if (isNative) {
      this.crashlytics.attachGlobalHandlers();
      await this.crashlytics.log('Crashlytics initialized');
      // 開発ビルド時はデバッグフックを登録（コンソールから動作確認できる）
      if (!environment.production) {
        await this.crashlytics.log(
          'Crashlytics debug hooks registered',
          'info',
        );
      }
    }

    // Adjust: 初期化完了まで待機してから以降の処理（trackEvent が -1 にならないようにする）
    await this.adjustService.start();

    const user = this.authService.getStoredUser();
    if (user?.id) {
      await this.firebaseAnalyticsService.setUserId(user.id);
      await this.firebaseAnalyticsService.logEvent('user_login', {
        userId: user.id,
      });
      // Crashlytics にもユーザーを関連付け（有効時のみ送信）
      await this.crashlytics.setUserId(String(user.id));
      // Push 初期化は Home 遷移時に一度だけ実行（下記 setupPushInitOnNavigation 参照）
    }
  }

  /**
   * ルーティングの変更に応じてタイトルを更新する
   */
  private setupTitleChangeOnNavigation(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        map((route) => {
          const data = route.snapshot.data;
          const symbols = Object.getOwnPropertySymbols(data);
          const titleSymbol = symbols.find(
            (sym) => sym.toString() === 'Symbol(RouteTitle)',
          );
          return titleSymbol ? data[titleSymbol] : data['title'];
        }),
      )
      .subscribe((pageTitle: string | undefined) => {
        if (pageTitle) {
          this.titleService.set(pageTitle);
          this.firebaseAnalyticsService.logEvent('page_view', {
            title: pageTitle,
          });
        }
      });
  }

  /**
   * Home（例: /home 配下）へ到達した最初の1回だけ Push 通知を初期化する
   * - 未ログイン・未権限状態での初期化失敗を避ける
   * - 二重初期化を避けるためフラグで制御
   */
  private setupPushInitOnNavigation(): void {
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(async () => {
        if (this.pushInitDone) return;

        const url = this.router.url;
        const isHomeArea = url.startsWith('/tabs/home'); // 必要に応じてルートを調整

        if (!isHomeArea) return;

        const user = this.authService.getStoredUser();
        if (!user?.id) return;

        try {
          await this.pushNotificationService.initialize();
          this.pushInitDone = true;
        } catch (e) {
          // 必要に応じてロギングなど
          console.error('Push initialize failed:', e);
        }
      });
  }
}
